// import React, { useMemo } from "react";
// import { useApp } from "../../../context/AppContext";
// import StatCard from "../../../components/StatCard/StatCard";
// import PageHeader from "../../../components/PageHeader/PageHeader";
// import "./Dashboard.css";

// const AgentDashboard = () => {
//   // be defensive: these may be undefined in some states
//   const {
//     currentUser,
//     users = [],
//     orders = [],
//     loyalty = [],
//     redemptionRequests = [],
//   } = useApp();

//   const stats = useMemo(() => {
//     // normalize arrays to avoid crashes
//     const usersArr = Array.isArray(users) ? users : [];
//     const ordersArr = Array.isArray(orders) ? orders : [];
//     const loyaltyArr = Array.isArray(loyalty) ? loyalty : [];
//     const redemptionArr = Array.isArray(redemptionRequests) ? redemptionRequests : [];

//     // prefer _id (common in this codebase). fallback to id
//     const currentUserId = currentUser?._id ?? currentUser?.id ?? null;

//     // Get agent's loyalty data (match by user id field used by loyalty objects)
//     const agentLoyalty =
//       loyaltyArr.find((l) => String(l.userId ?? l.user_id ?? l.user) === String(currentUserId)) || null;

//     // Count referred customers (customers created after agent)
//     // If agent has no createdAt, treat agentCreatedAt as epoch 0 so no one is considered earlier
//     const agentCreatedAt = currentUser?.createdAt ? new Date(currentUser.createdAt) : new Date(0);

//     const referredCustomers = usersArr.filter((u) => {
//       const isCustomer = (u.role ?? u.user_type ?? "").toLowerCase() === "customer";
//       const createdAt = u.createdAt ? new Date(u.createdAt) : null;
//       if (!isCustomer || !createdAt) return false;
//       return createdAt.getTime() > agentCreatedAt.getTime();
//     }).length;

//     // phones of referred customers (guard phone presence)
//     const referredCustomerPhones = usersArr
//       .filter((u) => {
//         const isCustomer = (u.role ?? u.user_type ?? "").toLowerCase() === "customer";
//         const createdAt = u.createdAt ? new Date(u.createdAt) : null;
//         return isCustomer && createdAt && createdAt.getTime() > agentCreatedAt.getTime() && !!u.phone;
//       })
//       .map((u) => String(u.phone));

//     // Count completed orders from referred customers (guard fields)
//     const referredOrders = ordersArr.filter((o) => {
//       const phone = o.customerPhone ?? o.customer_phone ?? o.phone ?? "";
//       const status = (o.orderStatus ?? o.status ?? "").toString().toLowerCase();
//       return referredCustomerPhones.includes(String(phone)) && status === "completed";
//     }).length;

//     const totalPoints = Number(agentLoyalty?.points ?? agentLoyalty?.point ?? 0) || 0;
//     const pendingRedemptions = redemptionArr.filter((r) => (r.status ?? "").toString().toLowerCase() === "pending").length;

//     return {
//       referredCustomers,
//       referredOrders,
//       totalPoints,
//       pendingRedemptions,
//     };
//   }, [currentUser, users, orders, loyalty, redemptionRequests]);

//   return (
//     <div className="agent-dashboard">
//       <PageHeader title="Agent Dashboard" subtitle={`Welcome, ${currentUser?.name || "Agent"}`} />

//       <div className="dashboard-stats-grid">
//         <StatCard title="Referred Customers" value={stats.referredCustomers} subtitle="Total referrals" icon="👥" />
//         {/* <StatCard title="Completed Orders" value={stats.referredOrders} subtitle="Via referrals" icon="📦" /> */}
//         <StatCard
//           title="Points available"
//           value={Number(stats.totalPoints).toLocaleString("en-IN")}
//           subtitle="Loyalty points earned"
//           icon="⭐"
//         />
//         <StatCard title="Earnings" value={stats.pendingRedemptions} subtitle="Total earnings" icon="💰" />
//       </div>
//     </div>
//   );
// };

// export default AgentDashboard;




// src/pages/AgentDashboard/AgentDashboard.jsx
import React, { useMemo, useEffect } from "react";
import { useApp } from "../../../context/AppContext";
import StatCard from "../../../components/StatCard/StatCard";
import PageHeader from "../../../components/PageHeader/PageHeader";
import "./Dashboard.css";

const AgentDashboard = () => {
  // be defensive: these may be undefined in some states
  const {
    currentUser,
    users = [],
    orders = [],
    loyalty = [],
    redemptionRequests = [],
    agentDashboardStats,        // new: data from backend { total_earnings, loyal_points_available, referred_count }
    fetchAgentDashboardStats,   // new: function to call backend
  } = useApp();

  // fetch agent stats from server on mount (if function available)
  useEffect(() => {
    if (typeof fetchAgentDashboardStats === "function") {
      // ignore errors at UI layer; context handles them
      fetchAgentDashboardStats().catch(() => {});
    }
  }, [fetchAgentDashboardStats]);

  // local fallback calculation (same logic you had) — used if agentDashboardStats is empty
  const localStats = useMemo(() => {
    const usersArr = Array.isArray(users) ? users : [];
    const ordersArr = Array.isArray(orders) ? orders : [];
    const loyaltyArr = Array.isArray(loyalty) ? loyalty : [];
    const redemptionArr = Array.isArray(redemptionRequests) ? redemptionRequests : [];

    const currentUserId = currentUser?._id ?? currentUser?.id ?? null;
    const agentLoyalty =
      loyaltyArr.find((l) => String(l.userId ?? l.user_id ?? l.user) === String(currentUserId)) || null;

    const agentCreatedAt = currentUser?.createdAt ? new Date(currentUser.createdAt) : new Date(0);

    const referredCustomers = usersArr.filter((u) => {
      const isCustomer = (u.role ?? u.user_type ?? "").toLowerCase() === "customer";
      const createdAt = u.createdAt ? new Date(u.createdAt) : null;
      if (!isCustomer || !createdAt) return false;
      return createdAt.getTime() > agentCreatedAt.getTime();
    }).length;

    const referredCustomerPhones = usersArr
      .filter((u) => {
        const isCustomer = (u.role ?? u.user_type ?? "").toLowerCase() === "customer";
        const createdAt = u.createdAt ? new Date(u.createdAt) : null;
        return isCustomer && createdAt && createdAt.getTime() > agentCreatedAt.getTime() && !!u.phone;
      })
      .map((u) => String(u.phone));

    const referredOrders = ordersArr.filter((o) => {
      const phone = o.customerPhone ?? o.customer_phone ?? o.phone ?? "";
      const status = (o.orderStatus ?? o.status ?? "").toString().toLowerCase();
      return referredCustomerPhones.includes(String(phone)) && status === "completed";
    }).length;

    const totalPoints = Number(agentLoyalty?.points ?? agentLoyalty?.point ?? 0) || 0;
    const pendingRedemptions = redemptionArr.filter((r) => (r.status ?? "").toString().toLowerCase() === "pending").length;

    // For earnings fallback we reuse pendingRedemptions (not ideal) — real earnings come from backend.
    return {
      referredCustomers,
      referredOrders,
      totalPoints,
      pendingRedemptions,
      totalEarningsFallback: 0,
    };
  }, [currentUser, users, orders, loyalty, redemptionRequests]);

  // Decide which data to render: prefer server-provided stats (agentDashboardStats.data)
  const shownStats = useMemo(() => {
    // agentDashboardStats might be shape: { total_earnings, loyal_points_available, referred_count }
    if (agentDashboardStats && (agentDashboardStats.total_earnings !== undefined || agentDashboardStats.referred_count !== undefined)) {
      return {
        referredCustomers: agentDashboardStats.referred_count ?? localStats.referredCustomers,
        referredOrders: localStats.referredOrders,
        totalPoints: agentDashboardStats.loyal_points_available ?? localStats.totalPoints,
        totalEarnings: agentDashboardStats.total_earnings ?? localStats.totalEarningsFallback,
      };
    }
    // fallback to local calculations
    return {
      referredCustomers: localStats.referredCustomers,
      referredOrders: localStats.referredOrders,
      totalPoints: localStats.totalPoints,
      totalEarnings: localStats.totalEarningsFallback,
    };
  }, [agentDashboardStats, localStats]);

  return (
    <div className="agent-dashboard">
      <PageHeader title="Agent Dashboard" subtitle={`Welcome, ${currentUser?.name || "Agent"}`} />

      <div className="dashboard-stats-grid">
        <StatCard title="Referred Customers" value={shownStats.referredCustomers} subtitle="Total referrals" icon="👥" />
        {/* <StatCard title="Completed Orders" value={shownStats.referredOrders} subtitle="Via referrals" icon="📦" /> */}
        <StatCard
          title="Points available"
          value={Number(shownStats.totalPoints).toLocaleString("en-IN")}
          subtitle="Loyalty points"
          icon="⭐"
        />
        <StatCard title="Earnings" value={Number(shownStats.totalEarnings).toLocaleString("en-IN")} subtitle="Total earnings" icon="💰" />
      </div>
    </div>
  );
};

export default AgentDashboard;
